import React, {useEffect, useRef} from 'react';
import {RefreshControl, ScrollView, View} from 'react-native';
import {AppState} from 'react-native';
import {useNavigation} from '@react-navigation/native';
import {createStackNavigator} from '@react-navigation/stack';
import {noop} from 'lodash';

import LoadingIndicator from '/component/base/LoadingIndicator';
import KeyboardAvoidingView from '/component/layout/KeyboardAvoidingView';
import ModalHeader from '/component/layout/ModalHeader';
import LastChangeDateComponent from '/component/partial/LastChangeDateComponent';
import {REASSIGNED} from '/enum/candidates.enum';
import {createPopoverProvider} from '/provider/PopoverProvider';
import {black} from '/theme/color/on.color';
import {generateComponentCallback} from '/util/component.util';
import {shouldReleaseFeature} from '/util/featureRelease.util';

import {ReassignedBanner} from './ReassignedBanner/ReassignedBanner';
import {AddCheckInReminder} from './Reminder/AddCheckInReminder';
import {CheckInReminder} from './Reminder/CheckInReminder';
import {EditCheckInReminder} from './Reminder/EditCheckInReminder';
import {AcceptDeclineBanner} from './AcceptDeclineBanner';
import {CandidateEditProfile} from './CandidateEditProfile';
import {useCandidateProfile, useRefresh} from './CandidateProfile.hook';
import {styles} from './CandidateProfile.style';
import {Contact} from './Contact';
import EditCandidateNotes from './EditCandidateNotes';
import {Header} from './Header';
import {MergeBanner} from './MergeBanner';
import {FirstMergeScreen, SecondMergeScreen} from './MergeCandidate';
import Notes from './Notes';
interface CandidateProfileProps {
  recordId: string | null;
  onClose: () => void;
  hasProfile: boolean;
  fetchData: () => void;
  featureFlag: {
    showCandidateNotes?: boolean;
    showEditCandidate?: boolean;
    showMergeCandidate?: boolean;
    ff_accept_decline?: string;
    ff_check_in_reminder?: string;
  };
  refreshCachedMetrics: () => void;
  resetCandidatesData: () => void;
  reportName: string;
  isPendingAcceptance: boolean;
  candidateName: string;
  dispositionDescription: string;
  dispositionOptionArray: any[];
  getDispositionOptions: () => void;
  hasdispositionOptions: boolean;
  dispositionTypeId: string;
  saveDispositionData: () => void;
  status: string;
  hasCandidateData: boolean;
}

export const CandidateProfile: React.FC<CandidateProfileProps> = ({
  hasProfile = false,
  fetchData = noop,
  onClose = noop,
  recordId = null,
  featureFlag = {
    showCandidateNotes: true,
    showEditCandidate: true,
    showMergeCandidate: false,
  },
  refreshCachedMetrics = () => {},
  resetCandidatesData = noop,
  reportName = '',
  isPendingAcceptance = false,
  candidateName = '',
  dispositionDescription = '',
  dispositionOptionArray = [],
  getDispositionOptions = noop,
  hasdispositionOptions = false,
  dispositionTypeId = '',
  saveDispositionData = noop,
  status = '',
  hasCandidateData = false,
}) => {
  const popoverProvider = createPopoverProvider();
  const {tt} = useCandidateProfile({
    hasProfile,
    fetchData,
    getDispositionOptions,
    hasdispositionOptions,
  });
  const navigation = useNavigation();
  const {Provider: PopoverProvider} = popoverProvider;
  const {refreshing, onRefreshRef} = useRefresh(async () => {
    refreshCachedMetrics();
  });
  const appState = useRef(AppState.currentState);

  const {ff_accept_decline = '', ff_check_in_reminder = ''} = featureFlag;
  const isAcceptDeclineReadyForRelease =
    shouldReleaseFeature(ff_accept_decline);

  const isCheckInReminderReadyForRelease =
    shouldReleaseFeature(ff_check_in_reminder);

  useEffect(() => {
    const subscription = AppState.addEventListener('change', nextAppState => {
      if (
        appState.current.match(/inactive|background/) &&
        nextAppState === 'active'
      ) {
        resetCandidatesData();
      }

      appState.current = nextAppState;
    });

    return () => {
      subscription.remove();
    };
  }, [resetCandidatesData]);

  const renderLoading = () => {
    return (
      <ModalHeader
        isProfileScreen
        onClose={onClose}
        closeIconName={'chevron-left'}
        style={{base: styles.base}}>
        <View style={[styles.content, styles.contentLoading]}>
          <LoadingIndicator size="large" />
        </View>
      </ModalHeader>
    );
  };

  const screenComponent = ({popoverShow}: any) => {
    return (
      <ModalHeader
        isProfileScreen
        onClose={onClose}
        {...(status !== REASSIGNED && {closeIconName: 'chevron-left'})}
        title={
          status !== REASSIGNED
            ? tt('candidateProfile', 'Candidate profile')
            : ''
        }
        actionLabel={
          featureFlag.showEditCandidate &&
          !isPendingAcceptance &&
          hasCandidateData &&
          status !== REASSIGNED &&
          tt('editProfile', 'Edit Profile')
        }
        onActionPress={() =>
          navigation.navigate('candidate-profile-edit-screen')
        }
        style={{base: styles.base}}>
        {hasCandidateData && status !== REASSIGNED && (
          <Header candidateId={recordId} />
        )}
        <ScrollView
          style={styles.content}
          contentContainerStyle={
            hasCandidateData && status === REASSIGNED
              ? styles.scrollViewContainer
              : null
          }
          refreshControl={
            <RefreshControl
              tintColor={black}
              refreshing={refreshing}
              onRefresh={onRefreshRef}
            />
          }>
          {(hasCandidateData && status !== REASSIGNED) || !hasCandidateData ? (
            <>
              {featureFlag.showMergeCandidate && (
                <MergeBanner
                  candidateId={recordId}
                  popoverShow={popoverShow}
                  onMergePress={() => navigation.navigate('first-merge-screen')}
                />
              )}
              {isAcceptDeclineReadyForRelease && isPendingAcceptance && (
                <AcceptDeclineBanner
                  candidateId={recordId}
                  popoverShow={popoverShow}
                  reportName={reportName}
                />
              )}
              <Contact
                candidateId={recordId}
                isArrow
                popoverShow={popoverShow}
                hideSocials={isPendingAcceptance}
                maskContacts={isPendingAcceptance}
                dispositionTypeId={dispositionTypeId}
                saveDispositionData={saveDispositionData}
                dispositionDescription={dispositionDescription}
                dispositionOptionArray={dispositionOptionArray}
                isPendingAcceptance={isPendingAcceptance}
                isCandidate={true}
              />
              {!isPendingAcceptance && isCheckInReminderReadyForRelease && (
                <CheckInReminder
                  candidateId={recordId}
                  candidateName={candidateName}
                />
              )}
              {featureFlag.showCandidateNotes && !isPendingAcceptance && (
                <Notes
                  showEditNotesLabel={hasCandidateData}
                  candidateId={recordId}
                  disabled={true}
                  greyOutTextInputValue={false}
                  onPress={() =>
                    navigation.navigate('candidate-notes-edit-screen')
                  }
                  FooterComponent={LastChangeDateComponent}
                />
              )}
            </>
          ) : (
            hasCandidateData && status === REASSIGNED && <ReassignedBanner />
          )}

          <View style={styles.sections} />
        </ScrollView>
      </ModalHeader>
    );
  };

  const getMainScreenComponent = () => {
    const {Consumer: PopoverConsumer} = popoverProvider;

    return () => {
      return (
        <PopoverConsumer>
          {({popoverShow}: any) => {
            const ScreenComponent = screenComponent;

            return <ScreenComponent popoverShow={popoverShow} />;
          }}
        </PopoverConsumer>
      );
    };
  };

  const editComponent = useRef(({popoverShow}: any) => {
    return (
      <CandidateEditProfile candidateId={recordId} popoverShow={popoverShow} />
    );
  });

  const candidateEditScreenComponent = () => {
    const {Consumer: PopoverConsumer} = popoverProvider;

    return () => {
      return (
        <PopoverConsumer>
          {({popoverShow}: any) => {
            const EditComponent = editComponent.current;

            return <EditComponent popoverShow={popoverShow} />;
          }}
        </PopoverConsumer>
      );
    };
  };

  const candidateEditNotesComponent = () => {
    return () => {
      return <EditCandidateNotes recordId={recordId} />;
    };
  };

  const getFirstMergeScreenComponent = () => {
    return () => {
      return (
        <FirstMergeScreen
          candidateId={recordId}
          onClosePress={() =>
            navigation.navigate('candidate-profile-main-screen')
          }
          onNextPress={(appendPhoneNumber: any) =>
            navigation.navigate('second-merge-screen', {
              appendPhoneNumber: appendPhoneNumber,
            })
          }
        />
      );
    };
  };

  const secondMergeComponent = useRef(({popoverShow, route}: any) => {
    return (
      <SecondMergeScreen
        recordId={recordId}
        popoverShow={popoverShow}
        onClosePress={() =>
          navigation.navigate('candidate-profile-main-screen')
        }
        route={route}
      />
    );
  });

  const getSecondMergeScreenComponent = () => {
    const {Consumer: PopoverConsumer} = popoverProvider;

    return ({route}: any) => {
      return (
        <PopoverConsumer>
          {({popoverShow}: any) => {
            const SecondMerge = secondMergeComponent.current;

            return <SecondMerge popoverShow={popoverShow} route={route} />;
          }}
        </PopoverConsumer>
      );
    };
  };

  const getCandidateProfileStackComponent = () => {
    const ProfileStackNavigator = createStackNavigator();
    const MainScreen = getMainScreenComponent();
    const EditProfile = candidateEditScreenComponent();
    const EditNotesScreen = candidateEditNotesComponent();
    const FirstMerge = getFirstMergeScreenComponent();
    const SecondMerge = getSecondMergeScreenComponent();

    return generateComponentCallback({
      component: (
        <ProfileStackNavigator.Navigator
          initialRouteName="candidate-profile-main-screen"
          screenOptions={{headerShown: false}}>
          <ProfileStackNavigator.Screen
            name="candidate-profile-main-screen"
            component={MainScreen}
          />
          <ProfileStackNavigator.Screen
            name="candidate-profile-edit-screen"
            component={EditProfile}
          />
          <ProfileStackNavigator.Screen
            name="candidate-notes-edit-screen"
            component={EditNotesScreen}
          />
          <ProfileStackNavigator.Screen
            name="first-merge-screen"
            component={FirstMerge}
          />
          <ProfileStackNavigator.Screen
            name="second-merge-screen"
            component={SecondMerge}
          />
          <ProfileStackNavigator.Screen
            name="add-checkin-reminder-screen"
            component={AddCheckInReminder}
          />
          <ProfileStackNavigator.Screen
            name="edit-checkin-reminder-screen"
            component={EditCheckInReminder}
          />
        </ProfileStackNavigator.Navigator>
      ),
    });
  };

  const ProfileStack = getCandidateProfileStackComponent();

  return (
    <PopoverProvider>
      {!hasProfile ? (
        renderLoading()
      ) : (
        <KeyboardAvoidingView>
          <ProfileStack />
        </KeyboardAvoidingView>
      )}
    </PopoverProvider>
  );
};

export default CandidateProfile;
